/**
 * GiftNotificationSystem — creator side only
 *
 * Receives pushed notification events for unread gift_received notifications
 * and fires the GiftCelebrationOverlay animation. A quiet fallback poll catches
 * missed reconnects and offline-login notifications.
 *
 * • Creator online  → sees animation as soon as the SSE notification arrives
 * • Creator offline → old unread gifts remain in the bell/list but do not replay
 *   animation or sound on login.
 *
 * Deduplication via localStorage so the animation never fires twice for the
 * same notification, even across page refreshes.
 */

import React, { useEffect, useRef } from 'react';
import { useAppSelector } from '../../hooks/redux';
import api from '../../services/api';
import { triggerGiftCelebration } from './GiftCelebrationOverlay';
import {
  getShownNotificationIds,
  GIFT_NOTIFICATION_GUARD_KEY,
  isNotificationFromCurrentSession,
  markNotificationShown,
  markNotificationsShown,
  normalizeNotificationId,
} from '../../utils/notificationDisplayGuard';

const FALLBACK_POLL_INTERVAL_MS = 60_000;
const GIFT_TYPES = new Set(['gift_received', 'gift_sent_confirmation']);

const GiftNotificationSystem: React.FC = () => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const shownRef = useRef<Set<string>>(getShownNotificationIds(GIFT_NOTIFICATION_GUARD_KEY));
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionStartedAtRef = useRef(Date.now());

  useEffect(() => {
    // Only run for authenticated creators (role === 'model')
    if (!isAuthenticated || user?.role !== 'model') return;

    sessionStartedAtRef.current = Date.now();
    shownRef.current = getShownNotificationIds(GIFT_NOTIFICATION_GUARD_KEY);

    const processNotifications = (data: any[]) => {
      data.forEach((n: any) => {
        if (!GIFT_TYPES.has(n.type)) return;
        if (n.is_read) return;
        const id = normalizeNotificationId(n.id);
        if (!id || shownRef.current.has(id)) return;

        shownRef.current.add(id);
        markNotificationShown(id, GIFT_NOTIFICATION_GUARD_KEY);

        if (!isNotificationFromCurrentSession(n.created_at, sessionStartedAtRef.current)) {
          return;
        }

        const emoji =
          n.data?.sticker_emoji ||
          n.data?.emoji ||
          '🎁';
        const quantity = n.data?.quantity || 1;

        triggerGiftCelebration(emoji, quantity);
      });
    };

    const poll = async () => {
      try {
        const response = await api.get('/notifications/', {
          params: { _t: Date.now() },
        });
        const data = response.data?.notifications ?? response.data;
        if (!Array.isArray(data)) return;

        processNotifications(data);
      } catch {
        // silent — never break the app
      }
    };

    const primeExistingGiftNotifications = async () => {
      try {
        const response = await api.get('/notifications/', {
          params: { _t: Date.now() },
        });
        const data = response.data?.notifications ?? response.data;
        if (!Array.isArray(data)) return;

        const idsToPrime = data
          .filter((n: any) => GIFT_TYPES.has(n.type) && !n.is_read)
          .map((n: any) => n.id);

        if (idsToPrime.length > 0) {
          shownRef.current = markNotificationsShown(idsToPrime, GIFT_NOTIFICATION_GUARD_KEY);
        }
      } catch {
        // silent — never break the app
      }
    };

    const handleRealtimeNotifications = (event: Event) => {
      const notifications = (event as CustomEvent<any[]>).detail;
      if (Array.isArray(notifications)) {
        processNotifications(notifications);
      }
    };

    primeExistingGiftNotifications();
    // Fallback catches reconnect misses without replaying startup backlog.
    intervalRef.current = setInterval(poll, FALLBACK_POLL_INTERVAL_MS);
    window.addEventListener('issac:notifications', handleRealtimeNotifications as EventListener);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('issac:notifications', handleRealtimeNotifications as EventListener);
    };
  }, [isAuthenticated, user?.role]);

  return null;
};

export default GiftNotificationSystem;
